/**
 * Parser Settings Page
 * 
 * Centralized settings for all parsers (Yandex, etc.)
 */

import { useState, useEffect, useCallback } from 'react'
import {
  Tabs,
  Card,
  Button,
  Upload,
  Form,
  Input,
  Space,
  Row,
  Col,
  Typography,
  Alert,
  Badge,
  Tag,
  Popconfirm,
  message,
  Spin,
  Empty,
  Divider,
  Table,
  Statistic,
  Tooltip,
  Modal,
} from 'antd'
import {
  UploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  DeleteOutlined,
  ExperimentOutlined,
  SafetyOutlined,
  ClockCircleOutlined,
  KeyOutlined,
  InboxOutlined,
  RocketOutlined,
  SettingOutlined,
  SyncOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  HistoryOutlined,
  DownloadOutlined,
  ChromeOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import type { UploadFile, UploadProps } from 'antd'
import {
  getCredentialsList,
  saveBrowserSession,
  updateCredentials,
  deleteCredentials,
  testCredentials,
  parseSessionFile,
  extractYandexLogin,
  type YandexCredentials,
} from '../services/yandexAuthService'
import UnifiedParsingModal from '../components/UnifiedParsingModal'
import ActiveParsingTasks from '../components/ActiveParsingTasks'
import {
  getSyncHistory,
  getSyncStats,
  getActiveProjects,
  triggerSyncAll,
  triggerSyncProject,
  type VDLSyncHistory,
  type VDLSyncStats,
  type ActiveProject,
} from '../services/vdlSyncService'
import type { ColumnsType } from 'antd/es/table'
import { Link } from 'react-router-dom'

const { Title, Text, Paragraph } = Typography
const { Dragger } = Upload

export default function ParserSettingsPage() {
  const [credentials, setCredentials] = useState<YandexCredentials[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [sessionData, setSessionData] = useState<any>(null)
  const [form] = Form.useForm()
  const [parsingModalVisible, setParsingModalVisible] = useState(false)
  const [selectedCredentialsForParsing, setSelectedCredentialsForParsing] = useState<number | null>(null)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [selectedTaskProjectId, setSelectedTaskProjectId] = useState<number | null>(null)
  const [selectedTaskCrmId, setSelectedTaskCrmId] = useState<number | null>(null)
  const [selectedTaskProjectName, setSelectedTaskProjectName] = useState<string | null>(null)
  const [extensionModalVisible, setExtensionModalVisible] = useState(false)

  // VDL Sync state
  const [syncHistory, setSyncHistory] = useState<VDLSyncHistory[]>([])
  const [syncStats, setSyncStats] = useState<VDLSyncStats | null>(null)
  const [activeProjects, setActiveProjects] = useState<ActiveProject[]>([])
  const [syncLoading, setSyncLoading] = useState(false)
  const [syncHistoryLoading, setSyncHistoryLoading] = useState(false)
  const [syncHistoryPage, setSyncHistoryPage] = useState(1)
  const [syncHistoryTotal, setSyncHistoryTotal] = useState(0)
  const [isSyncing, setIsSyncing] = useState(false)

  useEffect(() => {
    loadCredentials()
    
    // * Check for active parsing task in localStorage
    const activeTask = localStorage.getItem('active_parsing_task')
    if (activeTask) {
      try {
        const taskData = JSON.parse(activeTask)
        // * Auto-open modal with active task
        setSelectedTaskId(taskData.task_id)
        setParsingModalVisible(true)
        message.info('Восстановлено активное задание парсинга')
      } catch (error) {
        console.error('Failed to parse active task:', error)
        localStorage.removeItem('active_parsing_task')
      }
    }
  }, [])

  const loadCredentials = async () => {
    try {
      setIsLoading(true)
      const data = await getCredentialsList()
      setCredentials(data.items)
    } catch (error) {
      message.error('Ошибка загрузки credentials')
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  // VDL Sync functions
  const loadSyncData = useCallback(async () => {
    setSyncLoading(true)
    try {
      const [statsData, projectsData] = await Promise.all([
        getSyncStats(),
        getActiveProjects(),
      ])
      setSyncStats(statsData)
      setActiveProjects(projectsData)
    } catch (error) {
      console.error('Error loading sync data:', error)
    } finally {
      setSyncLoading(false)
    }
  }, [])

  const loadSyncHistory = useCallback(async (page: number = 1) => {
    setSyncHistoryLoading(true)
    try {
      const data = await getSyncHistory({ page, per_page: 10 })
      setSyncHistory(data.items)
      setSyncHistoryTotal(data.total)
      setSyncHistoryPage(page)
    } catch (error) {
      console.error('Error loading sync history:', error)
    } finally {
      setSyncHistoryLoading(false)
    }
  }, [])

  const handleTriggerSyncAll = async () => {
    setIsSyncing(true)
    try {
      const result = await triggerSyncAll()
      if (result.success) {
        message.success(result.message)
        loadSyncData()
        loadSyncHistory()
      } else {
        message.error(result.message)
      }
    } catch (error) {
      message.error('Ошибка запуска синхронизации')
    } finally {
      setIsSyncing(false)
    }
  }

  const handleTriggerSyncProject = async (projectId: number) => {
    try {
      const result = await triggerSyncProject(projectId)
      if (result.success) {
        message.success(result.message)
        loadSyncData()
        loadSyncHistory()
      } else {
        message.error(result.message)
      }
    } catch (error) {
      message.error('Ошибка запуска синхронизации проекта')
    }
  }

  const formatSyncDate = (dateString: string | null) => {
    if (!dateString) return 'Никогда'
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    accept: '.json',
    fileList,
    beforeUpload: (file) => {
      const isJson = file.name.endsWith('.json')
      if (!isJson) {
        message.error('Пожалуйста, загрузите JSON файл')
        return Upload.LIST_IGNORE
      }

      const isLt10M = file.size / 1024 / 1024 < 10
      if (!isLt10M) {
        message.error('Файл должен быть меньше 10MB')
        return Upload.LIST_IGNORE
      }

      // * Read file
      const reader = new FileReader()
      reader.onload = async (e) => {
        try {
          const content = e.target?.result as string
          const data = parseSessionFile(content)
          const login = extractYandexLogin(data.cookies)
          
          setSessionData(data)
          form.setFieldsValue({
            account_name: login || `Аккаунт ${new Date().toLocaleDateString()}`,
            yandex_login: login || '',
          })
          
          message.success('Файл успешно прочитан')
        } catch (error) {
          message.error('Ошибка чтения файла: ' + (error instanceof Error ? error.message : 'Неизвестная ошибка'))
          setFileList([])
          return
        }
      }
      reader.readAsText(file)
      
      setFileList([file])
      return false // * Prevent auto upload
    },
    onRemove: () => {
      setFileList([])
      setSessionData(null)
      form.resetFields()
    },
  }

  const handleSave = async (values: { account_name: string; yandex_login: string }) => {
    if (!sessionData) {
      message.error('Сначала загрузите файл сессии')
      return
    }

    try {
      setIsUploading(true)
      
      const newCredential = await saveBrowserSession({
        account_name: values.account_name,
        yandex_login: values.yandex_login,
        session_cookies: sessionData.cookies,
        crc: sessionData.crc || null,
        balancer_id: sessionData.balancer_id || null,
      })

      setCredentials(prev => [newCredential, ...prev])
      
      // * Reset form
      setFileList([])
      setSessionData(null)
      form.resetFields()
      
      message.success('Credentials успешно сохранены!')
    } catch (error) {
      message.error('Ошибка сохранения: ' + (error instanceof Error ? error.message : 'Неизвестная ошибка'))
    } finally {
      setIsUploading(false)
    }
  }

  const handleToggleActive = async (id: number, isActive: boolean) => {
    try {
      const updated = await updateCredentials(id, { is_active: isActive })
      setCredentials(prev => prev.map(c => (c.id === id ? updated : c)))
      message.success(isActive ? 'Credentials активированы' : 'Credentials деактивированы')
    } catch (error) {
      message.error('Ошибка обновления')
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await deleteCredentials(id)
      setCredentials(prev => prev.filter(c => c.id !== id))
      message.success('Credentials удалены')
    } catch (error) {
      message.error('Ошибка удаления')
    }
  }

  const handleTest = async (id: number) => {
    try {
      const result = await testCredentials(id)
      
      if (result.valid) {
        message.success(`✅ Credentials валидны (до ${result.expires_at || 'неизвестно'})`)
      } else {
        message.warning(`❌ ${result.message}`)
      }
    } catch (error) {
      message.error('Ошибка тестирования')
    }
  }

  const handleStartParsing = (id: number) => {
    setSelectedCredentialsForParsing(id)
    setSelectedTaskId(null)
    setParsingModalVisible(true)
  }

  const handleViewTask = (info: { taskId: string, projectId: number | null, crmId: number, projectName: string }) => {
    setSelectedTaskId(info.taskId)
    setSelectedTaskProjectId(info.projectId)
    setSelectedTaskCrmId(info.crmId)
    setSelectedTaskProjectName(info.projectName)
    setSelectedCredentialsForParsing(null)
    setParsingModalVisible(true)
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Никогда'
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const yandexAuthTab = (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Upload Section */}
      <Card 
        title={<><UploadOutlined /> Загрузить credentials из браузера</>}
        extra={
          <Button 
            icon={<ReloadOutlined />}
            onClick={loadCredentials}
            loading={isLoading}
          >
            Обновить список
          </Button>
        }
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Chrome Extension — prominent instruction */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 12,
            padding: '28px 32px',
            color: '#fff',
            marginBottom: 8,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <RocketOutlined style={{ fontSize: 28 }} />
              <span style={{ fontSize: 22, fontWeight: 700 }}>Авторизация через расширение</span>
              <Tag color="green" style={{ marginLeft: 4, fontWeight: 600 }}>Рекомендуется</Tag>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 16,
              marginBottom: 20,
            }}>
              <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '14px 16px' }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>1.</div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Скачайте расширение</div>
                <div style={{ fontSize: 13, opacity: 0.9 }}>Нажмите кнопку ниже и установите в браузер</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '14px 16px' }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>2.</div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Откройте WordCraft</div>
                <div style={{ fontSize: 13, opacity: 0.9 }}>
                  <a href="https://webmaster.yandex.ru/site/efficiency/wordcraft/" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'underline' }}>
                    webmaster.yandex.ru → WordCraft
                  </a>
                </div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '14px 16px' }}>
                <div style={{ fontSize: 24, marginBottom: 4 }}>3.</div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Сделайте любой запрос</div>
                <div style={{ fontSize: 13, opacity: 0.9 }}>Расширение автоматически перехватит сессию</div>
              </div>
            </div>

            <Button
              type="default"
              icon={<DownloadOutlined />}
              onClick={() => setExtensionModalVisible(true)}
              size="large"
              style={{ fontWeight: 600, height: 48, paddingInline: 32, fontSize: 16 }}
            >
              Скачать и установить расширение
            </Button>
          </div>

          {/* Manual upload fallback */}
          <Alert
            message="Ручная загрузка (альтернативный способ)"
            description="Если расширение не подходит — загрузите файл wordcraft_session.json вручную через форму ниже."
            type="info"
            showIcon
          />

          {/* File Upload */}
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ fontSize: 48, color: '#1890ff' }} />
            </p>
            <p className="ant-upload-text">
              Нажмите или перетащите файл для загрузки
            </p>
            <p className="ant-upload-hint">
              Поддерживается только JSON файл (wordcraft_session.json)
            </p>
          </Dragger>

          {/* Form */}
          {sessionData && (
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="account_name"
                    label="Название аккаунта"
                    rules={[{ required: true, message: 'Введите название' }]}
                  >
                    <Input placeholder="Основной аккаунт" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="yandex_login"
                    label="Логин Yandex"
                    rules={[{ required: true, message: 'Введите логин' }]}
                    extra="Email, телефон или username"
                  >
                    <Input placeholder="email@yandex.ru или +79991234567" />
                  </Form.Item>
                </Col>
              </Row>

              {/* Display extracted tokens */}
              <Alert
                message="📦 Извлеченные данные из файла"
                description={
                  <Space direction="vertical" size="small">
                    <Text>🍪 Cookies: <strong>{sessionData.cookies?.length || 0}</strong></Text>
                    {sessionData.crc && (
                      <Text>🔐 CRC токен: <strong>{sessionData.crc.substring(0, 30)}...</strong></Text>
                    )}
                    {sessionData.balancer_id && (
                      <Text>🔢 Balancer ID: <strong>{sessionData.balancer_id}</strong></Text>
                    )}
                    {(!sessionData.crc || !sessionData.balancer_id) && (
                      <Text type="warning">⚠️ Отсутствуют CRC или Balancer ID - авторизуйтесь заново</Text>
                    )}
                  </Space>
                }
                type={sessionData.crc && sessionData.balancer_id ? 'success' : 'warning'}
                showIcon
                style={{ marginBottom: 16 }}
              />

              <Form.Item>
                <Space>
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    icon={<SafetyOutlined />}
                    loading={isUploading}
                    size="large"
                  >
                    Сохранить credentials
                  </Button>
                  <Button 
                    onClick={() => {
                      setFileList([])
                      setSessionData(null)
                      form.resetFields()
                    }}
                    disabled={isUploading}
                  >
                    Отмена
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          )}
        </Space>
      </Card>

      {/* Credentials List */}
      <Card 
        title={<>📋 Сохраненные credentials ({credentials.length})</>}
      >
        {isLoading && credentials.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Загрузка...</Text>
            </div>
          </div>
        ) : credentials.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Нет сохраненных credentials"
          >
            <Text type="secondary">
              Загрузите файл сессии выше, чтобы начать работу
            </Text>
          </Empty>
        ) : (
          <Row gutter={[16, 16]}>
            {credentials.map(credential => (
              <Col xs={24} sm={24} md={12} lg={8} xl={8} key={credential.id}>
                <Card
                  size="small"
                  title={
                    <Space>
                      <KeyOutlined />
                      <Text strong>{credential.account_name}</Text>
                    </Space>
                  }
                  extra={
                    <Space>
                      {credential.is_active ? (
                        <Badge status="success" text="Активен" />
                      ) : (
                        <Badge status="default" text="Неактивен" />
                      )}
                    </Space>
                  }
                  actions={[
                    <Button
                      key="parse"
                      type="primary"
                      size="small"
                      icon={<RocketOutlined />}
                      onClick={() => handleStartParsing(credential.id)}
                      disabled={!credential.is_active || !credential.is_session_valid}
                    >
                      Запустить парсинг
                    </Button>,
                    <Button
                      key="toggle"
                      type={credential.is_active ? 'default' : 'primary'}
                      size="small"
                      onClick={() => handleToggleActive(credential.id, !credential.is_active)}
                      icon={credential.is_active ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                    >
                      {credential.is_active ? 'Деактивировать' : 'Активировать'}
                    </Button>,
                    <Button
                      key="test"
                      size="small"
                      icon={<ExperimentOutlined />}
                      onClick={() => handleTest(credential.id)}
                    >
                      Тест
                    </Button>,
                    <Popconfirm
                      key="delete"
                      title="Удалить credentials?"
                      description="Это действие нельзя отменить"
                      onConfirm={() => handleDelete(credential.id)}
                      okText="Да"
                      cancelText="Нет"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>
                  ]}
                >
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div>
                      <Text type="secondary">Логин:</Text>
                      <br />
                      <Text>{credential.yandex_login}</Text>
                    </div>
                    
                    <Divider style={{ margin: '8px 0' }} />

                    <div>
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Space>
                          {credential.is_session_valid ? (
                            <Tag icon={<CheckCircleOutlined />} color="success">
                              Сессия валидна
                            </Tag>
                          ) : (
                            <Tag icon={<CloseCircleOutlined />} color="error">
                              Истекла
                            </Tag>
                          )}
                        </Space>
                        
                        {credential.crc_token_age_hours != null && (
                          <Space>
                            {credential.is_crc_token_valid ? (
                              <Tag icon={<CheckCircleOutlined />} color="success">
                                CRC токен: {credential.crc_token_age_hours.toFixed(1)}ч
                              </Tag>
                            ) : (
                              <Tag icon={<CloseCircleOutlined />} color="error">
                                CRC истек: {credential.crc_token_age_hours.toFixed(1)}ч
                              </Tag>
                            )}
                          </Space>
                        )}
                      </Space>
                    </div>

                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        <ClockCircleOutlined /> Последняя авторизация:
                      </Text>
                      <br />
                      <Text style={{ fontSize: 12 }}>
                        {formatDate(credential.last_auth_at)}
                      </Text>
                    </div>

                    {credential.last_error && (
                      <Alert
                        message="Ошибка"
                        description={credential.last_error}
                        type="error"
                        showIcon
                        style={{ marginTop: 8 }}
                      />
                    )}
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card>

      {/* Active Parsing Tasks */}
      <ActiveParsingTasks onViewTask={handleViewTask} />

      {/* Footer note */}
      <Alert
        message={
          <Text>
            Credentials автоматически используются yandex-parser
            при указании <code>YANDEX_CREDENTIALS_ID</code> в конфигурации.
          </Text>
        }
        type="info"
        showIcon
      />
    </Space>
  )

  // VDL Sync history table columns
  const syncHistoryColumns: ColumnsType<VDLSyncHistory> = [
    {
      title: 'Проект',
      dataIndex: 'project_name',
      key: 'project_name',
      render: (name: string, record: VDLSyncHistory) => (
        <Space direction="vertical" size={0}>
          <Link to={`/projects/${record.crm_id}/sources`} style={{ fontWeight: 500 }}>
            {name || `Проект #${record.crm_id}`}
          </Link>
          <Text type="secondary" style={{ fontSize: 11 }}>CRM: {record.crm_id}</Text>
        </Space>
      ),
    },
    {
      title: 'Тип',
      dataIndex: 'sync_type',
      key: 'sync_type',
      width: 100,
      render: (type: string) => (
        <Tag color={type === 'auto' ? 'blue' : 'green'}>
          {type === 'auto' ? 'Авто' : 'Ручная'}
        </Tag>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const config: Record<string, { color: string; icon: React.ReactNode }> = {
          completed: { color: 'success', icon: <CheckCircleOutlined /> },
          running: { color: 'processing', icon: <SyncOutlined spin /> },
          failed: { color: 'error', icon: <CloseCircleOutlined /> },
        }
        const { color, icon } = config[status] || { color: 'default', icon: null }
        return (
          <Tag color={color} icon={icon}>
            {status === 'completed' ? 'Успешно' : status === 'running' ? 'В процессе' : 'Ошибка'}
          </Tag>
        )
      },
    },
    {
      title: 'Результат',
      key: 'result',
      width: 200,
      render: (_: unknown, record: VDLSyncHistory) => {
        if (record.status === 'failed') {
          return (
            <Tooltip title={record.error_message}>
              <Text type="danger" style={{ fontSize: 12 }}>
                {record.error_message?.substring(0, 30)}...
              </Text>
            </Tooltip>
          )
        }
        return (
          <Space size="small">
            {record.sources_created ? (
              <Tag color="green" style={{ fontSize: 11 }}>+{record.sources_created}</Tag>
            ) : null}
            {record.sources_updated ? (
              <Tag color="blue" style={{ fontSize: 11 }}>~{record.sources_updated}</Tag>
            ) : null}
            {record.sources_deleted ? (
              <Tag color="red" style={{ fontSize: 11 }}>-{record.sources_deleted}</Tag>
            ) : null}
          </Space>
        )
      },
    },
    {
      title: 'Время',
      key: 'time',
      width: 150,
      render: (_: unknown, record: VDLSyncHistory) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>{formatSyncDate(record.started_at)}</Text>
          {record.duration_seconds && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.duration_seconds}с
            </Text>
          )}
        </Space>
      ),
    },
  ]

  // Active projects table columns
  const activeProjectsColumns: ColumnsType<ActiveProject> = [
    {
      title: 'Проект',
      dataIndex: 'project_name',
      key: 'project_name',
      render: (name: string, record: ActiveProject) => (
        <Space direction="vertical" size={0}>
          <Link to={`/projects/${record.crm_id}/sources`} style={{ fontWeight: 500 }}>
            {name}
          </Link>
          <Text type="secondary" style={{ fontSize: 11 }}>CRM: {record.crm_id}</Text>
        </Space>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => <Tag>{status}</Tag>,
    },
    {
      title: 'Источники',
      key: 'sources',
      width: 150,
      render: (_: unknown, record: ActiveProject) => (
        <Space size="small">
          <Tag color="blue">{record.total_sources}</Tag>
          <Tag color="green">{record.active_sources} акт.</Tag>
          <Tag color="orange">{record.pending_sources} ожид.</Tag>
        </Space>
      ),
    },
    {
      title: 'Последняя синхр.',
      dataIndex: 'last_sync_at',
      key: 'last_sync_at',
      width: 150,
      render: (date: string | null) => (
        <Text style={{ fontSize: 12 }}>{formatSyncDate(date)}</Text>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: ActiveProject) => (
        <Button
          size="small"
          icon={<SyncOutlined />}
          onClick={() => handleTriggerSyncProject(record.crm_id)}
        >
          Синхр.
        </Button>
      ),
    },
  ]

  const vdlSyncTab = (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Stats */}
      <Card
        title={<><SyncOutlined /> Статус автосинхронизации</>}
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                loadSyncData()
                loadSyncHistory()
              }}
              loading={syncLoading}
            >
              Обновить
            </Button>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={handleTriggerSyncAll}
              loading={isSyncing}
            >
              Запустить синхронизацию
            </Button>
          </Space>
        }
      >
        {syncLoading && !syncStats ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Spin />
          </div>
        ) : syncStats ? (
          <Row gutter={16}>
            <Col span={4}>
              <Statistic
                title="Автосинхронизация"
                value={syncStats.auto_sync_enabled ? 'Вкл' : 'Выкл'}
                valueStyle={{ color: syncStats.auto_sync_enabled ? '#52c41a' : '#ff4d4f' }}
                prefix={syncStats.auto_sync_enabled ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Интервал"
                value={syncStats.auto_sync_interval_minutes}
                suffix="мин"
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Активных проектов"
                value={syncStats.active_projects_count}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Синхронизаций сегодня"
                value={syncStats.today_syncs}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Успешных"
                value={syncStats.completed_syncs}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Ошибок"
                value={syncStats.failed_syncs}
                valueStyle={{ color: syncStats.failed_syncs > 0 ? '#ff4d4f' : undefined }}
              />
            </Col>
          </Row>
        ) : (
          <Empty description="Нет данных" />
        )}
      </Card>

      {/* Active Projects */}
      <Card
        title={<><HistoryOutlined /> Активные проекты для синхронизации ({activeProjects.length})</>}
      >
        <Table
          columns={activeProjectsColumns}
          dataSource={activeProjects}
          rowKey="id"
          size="small"
          pagination={false}
          loading={syncLoading}
          locale={{ emptyText: 'Нет активных проектов' }}
        />
      </Card>

      {/* Sync History */}
      <Card
        title={<><HistoryOutlined /> История синхронизаций</>}
      >
        <Table
          columns={syncHistoryColumns}
          dataSource={syncHistory}
          rowKey="id"
          size="small"
          loading={syncHistoryLoading}
          pagination={{
            current: syncHistoryPage,
            total: syncHistoryTotal,
            pageSize: 10,
            onChange: (page) => loadSyncHistory(page),
            showSizeChanger: false,
            showTotal: (total) => `Всего: ${total}`,
          }}
          locale={{ emptyText: 'История пуста' }}
        />
      </Card>

      {/* Info */}
      <Alert
        message="Автоматическая синхронизация"
        description={
          <div>
            Система автоматически синхронизирует источники с VDL для активных проектов каждые{' '}
            <strong>{syncStats?.auto_sync_interval_minutes || 30} минут</strong>.
            <br />
            Активными считаются проекты со статусом не "Завершён" и включенным "Сбором вебмастер".
          </div>
        }
        type="info"
        showIcon
      />
    </Space>
  )

  const handleTabChange = (key: string) => {
    if (key === 'vdl-sync') {
      loadSyncData()
      loadSyncHistory()
    }
  }

  const tabItems = [
    {
      key: 'yandex',
      label: (
        <span>
          <KeyOutlined /> Yandex Webmaster
        </span>
      ),
      children: yandexAuthTab,
    },
    {
      key: 'vdl-sync',
      label: (
        <span>
          <SyncOutlined /> VDL Синхронизация
        </span>
      ),
      children: vdlSyncTab,
    },
  ]

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <div>
          <Title level={2}>
            <SettingOutlined /> Настройки парсеров
          </Title>
          <Paragraph type="secondary">
            Управление учетными данными для работы с различными парсерами
          </Paragraph>
        </div>

        {/* Tabs */}
        <Tabs items={tabItems} defaultActiveKey="yandex" onChange={handleTabChange} />
      </Space>

      {/* Extension Install Modal */}
      <Modal
        title={
          <Space>
            <ChromeOutlined style={{ color: '#1890ff', fontSize: 20 }} />
            <span>Установка расширения для Яндекс.Вебмастер</span>
          </Space>
        }
        open={extensionModalVisible}
        onCancel={() => setExtensionModalVisible(false)}
        footer={null}
        width={680}
        centered
      >
        <Space direction="vertical" size="large" style={{ width: '100%', paddingTop: 8 }}>
          {/* Download */}
          <div style={{ textAlign: 'center', padding: '20px 0', background: '#f6f8fa', borderRadius: 8 }}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              size="large"
              onClick={async () => {
                try {
                  const resp = await fetch(`${window.location.origin}/extension/chrome-extension.zip`)
                  const blob = await resp.blob()
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = 'chrome-extension.zip'
                  a.click()
                  URL.revokeObjectURL(url)
                } catch {
                  window.open(`${window.location.origin}/extension/chrome-extension.zip`, '_blank')
                }
              }}
              style={{ fontWeight: 600, height: 48, paddingInline: 32, fontSize: 16 }}
            >
              Скачать ZIP-файл
            </Button>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">v1.0.0 — 14 КБ — Chrome, Яндекс.Браузер, Edge</Text>
            </div>
          </div>

          {/* Installation steps */}
          <div>
            <Title level={5} style={{ marginBottom: 12 }}>
              <SettingOutlined /> Установка (1 минута)
            </Title>

            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#1890ff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>1</div>
              <div>
                <Text strong>Скачайте и распакуйте ZIP</Text>
                <br />
                <Text type="secondary">Распакуйте архив в любую папку на компьютере</Text>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#1890ff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>2</div>
              <div>
                <Text strong>Откройте страницу расширений</Text>
                <br />
                <Text type="secondary">
                  Chrome: <Text code>chrome://extensions</Text> &nbsp;
                  Яндекс: <Text code>browser://extensions</Text> &nbsp;
                  Edge: <Text code>edge://extensions</Text>
                </Text>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#1890ff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>3</div>
              <div>
                <Text strong>Включите "Режим разработчика"</Text>
                <br />
                <Text type="secondary">Переключатель в правом верхнем углу страницы</Text>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#1890ff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>4</div>
              <div>
                <Text strong>"Загрузить распакованное расширение"</Text>
                <br />
                <Text type="secondary">Выберите папку, куда распаковали архив</Text>
              </div>
            </div>

            <Alert
              message="Для Яндекс.Браузера можно просто перетащить ZIP-файл прямо в окно браузера"
              type="warning"
              showIcon
              style={{ marginTop: 4 }}
            />
          </div>

          <Divider style={{ margin: 0 }} />

          {/* Usage */}
          <div>
            <Title level={5} style={{ marginBottom: 12 }}>
              <RocketOutlined /> Как пользоваться
            </Title>

            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#52c41a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>1</div>
              <div>
                <Text strong>Откройте WordCraft в Яндекс.Вебмастере</Text>
                <br />
                <a
                  href="https://webmaster.yandex.ru/site/efficiency/wordcraft/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 15, fontWeight: 500 }}
                >
                  webmaster.yandex.ru/site/efficiency/wordcraft/
                </a>
                <br />
                <Text type="secondary">Войдите в аккаунт если ещё не авторизованы</Text>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#52c41a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>2</div>
              <div>
                <Text strong>Сделайте любой запрос</Text>
                <br />
                <Text type="secondary">Введите любое слово и нажмите «Проанализировать». Расширение автоматически перехватит сессию и отправит на сервер.</Text>
              </div>
            </div>

            <Alert
              message="Готово! Иконка расширения покажет зелёный значок «OK». Сессия обновляется автоматически при каждом запросе."
              type="success"
              showIcon
              style={{ marginTop: 12 }}
            />
          </div>

          <Divider style={{ margin: 0 }} />

          {/* FAQ */}
          <div>
            <Title level={5} style={{ marginBottom: 8 }}>
              <QuestionCircleOutlined /> Частые вопросы
            </Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
              <Text strong>Как часто обновлять сессию?</Text> — Автоматически при каждом запросе в WordCraft. Если пользуетесь раз в день — ничего делать не нужно.
            </Text>
            <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
              <Text strong>Как сменить сервер?</Text> — Клик по иконке расширения → "Настройки" → измените URL.
            </Text>
            <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
              <Text strong>Иконка "ERR"?</Text> — Проверьте, доступен ли сервер RSA Toolbox. Кликните на иконку для деталей ошибки.
            </Text>
          </div>
        </Space>
      </Modal>

      {/* Unified Parsing Modal */}
      <UnifiedParsingModal
        visible={parsingModalVisible}
        credentialsId={selectedCredentialsForParsing}
        taskId={selectedTaskId}
        projectId={selectedTaskProjectId}
        projectCrmId={selectedTaskCrmId}
        projectName={selectedTaskProjectName}
        onClose={() => {
          setParsingModalVisible(false)
          setSelectedCredentialsForParsing(null)
          setSelectedTaskId(null)
          setSelectedTaskProjectId(null)
          setSelectedTaskCrmId(null)
          setSelectedTaskProjectName(null)
        }}
      />
    </div>
  )
}

